// src/components/AppleDateInput.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { A } from "../styles/appleClasses";

type Props = {
  valueYmd?: string | null;
  onChangeYmd: (ymd: string) => void;

  minYmd?: string;
  maxYmd?: string;

  allowEmpty?: boolean;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
  openCalendarLabel?: string;

  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;

  autoFocus?: boolean;
  inputClassName?: string;

  onBlur?: () => void;

  showCalendar?: boolean;
};

function isYmd(s: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function ymdToMdy(ymd: string) {
  if (!isYmd(ymd)) return "";
  const y = ymd.slice(0, 4);
  const m = ymd.slice(5, 7);
  const d = ymd.slice(8, 10);
  return `${m}/${d}/${y}`;
}

function daysInMonth(year: number, month1to12: number) {
  return new Date(year, month1to12, 0).getDate();
}

function mdyToYmd(mdyRaw: string): string | null {
  const s = String(mdyRaw || "").trim();
  if (!s) return null;

  const digitsOnly = s.replace(/[^\d]/g, "");
  if (digitsOnly.length === 8) {
    const mm = digitsOnly.slice(0, 2);
    const dd = digitsOnly.slice(2, 4);
    const yyyy = digitsOnly.slice(4, 8);

    const m = Number(mm);
    const d = Number(dd);
    const y = Number(yyyy);

    if (!Number.isFinite(m) || !Number.isFinite(d) || !Number.isFinite(y)) {
      return null;
    }
    if (y < 1900 || y > 2100) return null;
    if (m < 1 || m > 12) return null;

    const dim = daysInMonth(y, m);
    if (d < 1 || d > dim) return null;

    return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  const parts = s.split(/[\/\-\.\s]+/).filter(Boolean);
  if (parts.length !== 3) return null;

  const mmRaw = parts[0];
  const ddRaw = parts[1];
  const yyRaw = parts[2];

  const m = Number(mmRaw);
  const d = Number(ddRaw);
  let y = Number(yyRaw);

  if (!Number.isFinite(m) || !Number.isFinite(d) || !Number.isFinite(y)) {
    return null;
  }

  if (yyRaw.length === 2 && y >= 0 && y <= 99) {
    y = 2000 + y;
  } else if (yyRaw.length !== 4) {
    return null;
  }

  if (y < 1900 || y > 2100) return null;
  if (m < 1 || m > 12) return null;

  const dim = daysInMonth(y, m);
  if (d < 1 || d > dim) return null;

  return `${y}-${pad2(m)}-${pad2(d)}`;
}

function clampYmd(ymd: string, minYmd?: string, maxYmd?: string) {
  let out = ymd;
  if (minYmd && isYmd(minYmd) && out < minYmd) out = minYmd;
  if (maxYmd && isYmd(maxYmd) && out > maxYmd) out = maxYmd;
  return out;
}

function sanitizeDraft(raw: string) {
  let s = String(raw ?? "");
  s = s.replace(/[^\d/-]/g, "");
  if (s.length > 10) s = s.slice(0, 10);
  return s;
}

export function AppleDateInput({
  valueYmd,
  onChangeYmd,
  minYmd,
  maxYmd,
  allowEmpty = false,
  placeholder = "MM/DD/YYYY",
  ariaLabel,
  openCalendarLabel = "Open calendar",
  autoFocus,
  className = "",
  inputClassName,
  disabled,
  required,
  name,
  id,
  onBlur,
  showCalendar = true,
}: Props) {
  const ymd = String(valueYmd || "").trim();
  const display = useMemo(() => (ymd ? ymdToMdy(ymd) : ""), [ymd]);

  const [draft, setDraft] = useState(display);

  const isFocusedRef = useRef(false);
  const lastCommittedRef = useRef<string>(ymd);

  useEffect(() => {
    if (!isFocusedRef.current) setDraft(display);
  }, [display]);

  useEffect(() => {
    lastCommittedRef.current = ymd;
  }, [ymd]);

  const revert = () => {
    const committed = lastCommittedRef.current;
    setDraft(committed ? ymdToMdy(committed) : "");
  };

  const commit = () => {
    if (disabled) return;

    const raw = (draft || "").trim();

    if (!raw) {
      if (allowEmpty) {
        onChangeYmd("");
        lastCommittedRef.current = "";
        setDraft("");
      } else {
        revert();
      }
      return;
    }

    const parsed = mdyToYmd(raw);
    if (!parsed) {
      revert();
      return;
    }

    const next = clampYmd(parsed, minYmd, maxYmd);
    onChangeYmd(next);
    lastCommittedRef.current = next;
    setDraft(ymdToMdy(next));
  };

  const commitYmdDirect = (pickedYmd: string) => {
    if (disabled) return;

    const cleaned = String(pickedYmd || "").trim();

    if (!cleaned) {
      if (allowEmpty) {
        onChangeYmd("");
        lastCommittedRef.current = "";
        setDraft("");
      } else {
        revert();
      }
      return;
    }

    if (!isYmd(cleaned)) {
      revert();
      return;
    }

    const next = clampYmd(cleaned, minYmd, maxYmd);
    onChangeYmd(next);
    lastCommittedRef.current = next;
    setDraft(ymdToMdy(next));
  };

  const baseCls = clsx(
    A.inputBase,
    A.num,
    "appearance-none placeholder-gray-400",
    showCalendar ? "pr-12" : "",
    disabled && A.fieldDisabled,
    inputClassName
  );

  return (
    <div className={clsx("relative", className)}>
      <input
        id={id}
        name={name}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={draft}
        disabled={disabled}
        required={required}
        onFocus={() => {
          isFocusedRef.current = true;
        }}
        onChange={(e) => {
          setDraft(sanitizeDraft(e.target.value));
        }}
        onBlur={() => {
          isFocusedRef.current = false;
          commit();
          onBlur?.();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            revert();
            e.currentTarget.blur();
          }
        }}
        className={baseCls}
      />

      {showCalendar && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <div
            className={clsx(
              A.iconBtn,
              "flex h-10 w-10 items-center justify-center",
              disabled ? "cursor-not-allowed opacity-50" : "hover:bg-gray-100"
            )}
            aria-label={openCalendarLabel}
            title={openCalendarLabel}
          >
            <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
          </div>

          <input
            type="date"
            value={isYmd(ymd) ? ymd : ""}
            min={minYmd && isYmd(minYmd) ? minYmd : undefined}
            max={maxYmd && isYmd(maxYmd) ? maxYmd : undefined}
            disabled={disabled}
            onChange={(e) => commitYmdDirect(e.target.value)}
            className="absolute inset-0 cursor-pointer opacity-0"
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>
      )}
    </div>
  );
}
